"use client";
import { getProductNavigationUrl } from "../lib.js";
import { isBD } from "../../../utils/businessType.lib.js";
import { ECO_FEE_CATEGORY } from "../../../components/Product/EcoFee/constants.js";
import { ATTRIBUTE_TAXABLE_FOOD, ATTRIBUTE_BRAND, ATTRIBUTE_SWATCH_IMAGE_KEY } from "./constants.js";
import { getCompareProductButtonData, getReviewRatingData, getReplacementItemNumber, getImageUrlPCP, getProductFeaturesData, getProductDeliveryBadgeData, getProductActions, getDiscountsData, getPriceData, getPillBadges, getMemberOnlyData, getFsaEligibility, getChdiEligibility, getDeliveryChannelBadgeData, getColorSwatchesData, getInWareHouseChannelBadgeData, getInWarehouseAvailabilityText, getAddToListButtonData } from "./lib.js";
const normalizeProductSummaryApi = (responseData, productConfig, hasUserSignedId, cookieContext, keyword, isSponsored, isProductDetailsPage) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r;
  const {
    pillBadgeConfig,
    imageConfig,
    featureToggleConfig,
    enabledWarehousesListConfig,
    translationsConfig
  } = productConfig || {};
  const { inventoryResponseData, productApiResponseData } = responseData || {};
  const item = productApiResponseData && "productData" in productApiResponseData && Array.isArray(productApiResponseData.productData) ? productApiResponseData.productData[0] : void 0;
  if (!item) {
    return null;
  }
  const {
    id,
    descriptions,
    isLinkFeeEligible,
    isPublished,
    childCatalogData,
    groupedLinkFee,
    productAttributes,
    replacement
  } = item || {};
  const { languageKey, object: descriptionsObject } = descriptions == null ? void 0 : descriptions[0];
  const { shortDescription, marketingStatement, features } = descriptionsObject || {};
  const { warehouse, wcMember, deliveryLocation, bdDeliveryLocation } = cookieContext || {};
  const { nearestWarehouse } = warehouse || {};
  const location = isBD() ? (_a = bdDeliveryLocation == null ? void 0 : bdDeliveryLocation.bdLocation) == null ? void 0 : _a.slice(-2) : deliveryLocation == null ? void 0 : deliveryLocation.state;
  const linkFeePrices = ((_b = childCatalogData == null ? void 0 : childCatalogData[0]) == null ? void 0 : _b.groupedLinkFee) || groupedLinkFee || [];
  const relevantLinkFeePrice = linkFeePrices[location ? location : ""];
  const taxableFoodItem = (_d = (_c = productAttributes == null ? void 0 : productAttributes[0]) == null ? void 0 : _c.object.find(
    (attr) => attr.key === ATTRIBUTE_TAXABLE_FOOD
  )) == null ? void 0 : _d.value;
  const productBrand = (_f = (_e = productAttributes == null ? void 0 : productAttributes[0]) == null ? void 0 : _e.object.find(
    (attr) => attr.key === ATTRIBUTE_BRAND
  )) == null ? void 0 : _f.value;
  const ecoFee = relevantLinkFeePrice == null ? void 0 : relevantLinkFeePrice.find(
    (fee) => fee.feeCategory === ECO_FEE_CATEGORY
  );
  const bopiwWarehouseEnabled = Boolean(
    (_g = enabledWarehousesListConfig == null ? void 0 : enabledWarehousesListConfig.enabledWarehousesBOPIWList) == null ? void 0 : _g.some(
      (enabledWarehouseId) => enabledWarehouseId === (nearestWarehouse == null ? void 0 : nearestWarehouse.catalog)
    )
  );
  const wioWarehouseEnabled = Boolean(
    (_h = enabledWarehousesListConfig == null ? void 0 : enabledWarehousesListConfig.enabledWarehousesWIOList) == null ? void 0 : _h.some(
      (enabledWarehouseId) => enabledWarehouseId === (nearestWarehouse == null ? void 0 : nearestWarehouse.catalog)
    )
  );
  const hasSingleSku = (childCatalogData == null ? void 0 : childCatalogData.length) === 1;
  const publishedChildItems = {
    catalogData: childCatalogData == null ? void 0 : childCatalogData.filter(
      (cItem) => Boolean(cItem.isPublished)
    )
  };
  const isSwatchable = item.isSwatchable && (publishedChildItems == null ? void 0 : publishedChildItems.catalogData) && ((_i = publishedChildItems == null ? void 0 : publishedChildItems.catalogData) == null ? void 0 : _i.length) > 1;
  const primaryCatalogItem = isSwatchable ? (_j = publishedChildItems == null ? void 0 : publishedChildItems.catalogData) == null ? void 0 : _j[0] : item;
  const swatchAttr = (_m = (_l = (_k = primaryCatalogItem == null ? void 0 : primaryCatalogItem.productAttributes) == null ? void 0 : _k[0]) == null ? void 0 : _l.object.filter((aItem) => {
    return ATTRIBUTE_SWATCH_IMAGE_KEY in aItem && aItem[ATTRIBUTE_SWATCH_IMAGE_KEY];
  })) == null ? void 0 : _m[0];
  const { swatchImage, key } = swatchAttr || {};
  return {
    // only applicable to product with single sku
    addToListButtonData: getAddToListButtonData(
      item,
      shortDescription,
      hasSingleSku,
      cookieContext,
      publishedChildItems
    ),
    availabilityInfoData: {
      inWarehouseAvailabilityText: getInWarehouseAvailabilityText(item),
      inWarehouseChannelBadgeData: getInWareHouseChannelBadgeData(
        item,
        enabledWarehousesListConfig,
        cookieContext,
        (featureToggleConfig == null ? void 0 : featureToggleConfig.showMultiVariantsAvailability) || false,
        hasSingleSku,
        inventoryResponseData
      )
    },
    colorSwatchesData: getColorSwatchesData(
      translationsConfig,
      imageConfig,
      item,
      publishedChildItems,
      featureToggleConfig
    ),
    deliveryChannelData: {
      status: getDeliveryChannelBadgeData(
        item,
        bopiwWarehouseEnabled,
        wioWarehouseEnabled,
        inventoryResponseData
      )
    },
    isChdiEligible: getChdiEligibility(publishedChildItems),
    isEcoFeeEligible: isLinkFeeEligible,
    isFsaEligible: getFsaEligibility(publishedChildItems),
    isMemberOnlyItem: getMemberOnlyData(item, publishedChildItems),
    isPublished,
    locale: languageKey == null ? void 0 : languageKey.toLowerCase(),
    pillBadges: getPillBadges(
      item,
      pillBadgeConfig,
      imageConfig == null ? void 0 : imageConfig.domainUrl,
      keyword
    ),
    priceListData: getPriceData(
      item,
      hasSingleSku,
      cookieContext,
      !isPublished ? childCatalogData : publishedChildItems,
      ecoFee,
      hasUserSignedId,
      isProductDetailsPage
    ),
    discountsData: getDiscountsData(item),
    savingsData: {
      salePrice: (_o = (_n = childCatalogData == null ? void 0 : childCatalogData[0]) == null ? void 0 : _n.displayPrice) == null ? void 0 : _o.deliveredPrice,
      listPrice: (_q = (_p = childCatalogData == null ? void 0 : childCatalogData[0]) == null ? void 0 : _p.displayPrice) == null ? void 0 : _q.onlinePrice
    },
    ecoFee,
    productActions: getProductActions(
      item,
      featureToggleConfig,
      translationsConfig,
      hasUserSignedId,
      wioWarehouseEnabled,
      bopiwWarehouseEnabled,
      hasSingleSku,
      (wcMember == null ? void 0 : wcMember.value) || "",
      inventoryResponseData,
      publishedChildItems,
      isSponsored,
      cookieContext,
      isProductDetailsPage
    ),
    productDeliveryBadgeData: getProductDeliveryBadgeData(
      item,
      translationsConfig,
      imageConfig == null ? void 0 : imageConfig.domainUrl
    ),
    productInfoData: {
      itemNumber: id,
      partNumber: id,
      productBrand,
      productFeaturesData: getProductFeaturesData(features || ""),
      productImageUrl: getImageUrlPCP(item),
      productLinkUrl: getProductNavigationUrl(
        shortDescription,
        id,
        imageConfig == null ? void 0 : imageConfig.domainUrl,
        swatchImage,
        key,
        (_r = featureToggleConfig == null ? void 0 : featureToggleConfig.enableBModPDPNavigationUrl) == null ? void 0 : _r.isEnabled
      ),
      productName: shortDescription,
      replacementItemNumber: getReplacementItemNumber(replacement)
    },
    promoItem: {
      promoText: marketingStatement || "",
      promoTextColor: ""
    },
    reviewRatingData: getReviewRatingData(
      item,
      translationsConfig
    ),
    showCompareProductButton: getCompareProductButtonData(item),
    taxableFoodItem,
    linkFeePrices
  };
};
export {
  normalizeProductSummaryApi
};
